'use client'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import type { FC } from 'react'
import { useParams, usePathname, useSearchParams } from 'next/navigation'
import { motion, AnimatePresence } from 'framer-motion'
import styled, { css } from 'styled-components'
import { mq, vw, getP40, getP13 } from '@/styles'
import { useModal, useScrollTracker } from '@/hooks'
import { MODALS } from '@/context'
import { Modal } from './Modal'
import { Link, Figure } from '@/components'
import { dialogVariants, listVariants, listItemVariants, detailUlvariants, detailvariants } from './modalMobileMenuVariants'

type MenuProps = {
  hasBanners?: boolean
  data?: {
    name: string
    href: string
    type: keyof typeof itemByType
  }[]
}

const backIconLight = {
  type: 'svg' as const,
  src: '/images/svg/chevron--left--light.svg',
  alt: 'Go back icon',
  size: { width: 12, height: 12}
}

const iconChevronAccordion = {
  type: 'svg' as const,
  src: '/images/svg/chevron--select--light.svg',
  alt: 'Go back icon',
  size: { width: 12, height: 8}
}

const sablitoImage = {
  type: 'image',
  alt: 'Sablito in a plane image',
  default: {
    src: '/images/sablito.png',
    size: { width: 3056, height: 2429 }
  }
} as const

const DetailDropdown = ({ item, setLevel, level }) => {
  const [open, setOpen] = useState(false)
  const { toggle } = useModal(MODALS.MENU)
  const pathname = usePathname()
  const params = useParams()

  const onClick= (e) => {
    e.preventDefault()
    setOpen((prevState) => !prevState)
  }

  return (<Details {...(open && { open })} animate={open ? 'active' : 'inactive'} variants={detailvariants}>
    <summary>
      {item.name}
      <Figure media={iconChevronAccordion} onClick={onClick} fill={false} />
    </summary>
    <AnimatePresence mode='wait'>
      <motion.ul initial='inactive' animate={open ? 'active' : 'exit'} exit='exit' variants={detailUlvariants}>
        {getMenuItems(item.children, level, setLevel, true, pathname, params, toggle)}
      </motion.ul>
    </AnimatePresence>
  </Details>)
}

const itemByType = {
  'link': (item, setLevel, idx, level, currentPathname, _params, toggle) => {
    const active = currentPathname === item.href || currentPathname === item.altPathname

    if (!item.children) {
      const samePathname = (item.href === currentPathname || item.altPathname === currentPathname)
      if(samePathname) {
        return (<button type='button' className='active' onClick={toggle}>{item.name}</button>)
      } else {
        return (<Link href={item.href} {...(active && { className: 'active' })}>{item.name}</Link>)
      }
    }

    if(item.childrenAs === 'sublevel') {
      return (<><Link href={item.href}>{item.name}</Link><button onClick={() => { setLevel(prevLevel => [...prevLevel, idx]) }}>CHEVRON</button></>)
    } else if (item.childrenAs === 'accordion') {
      return <DetailDropdown item={item} setLevel={setLevel} level={level} />
    }
    return (<Link href={item.href}>{item.name}</Link>)
  },
  'button-level': (item, setLevel, idx) => <button onClick={() => { setLevel(prevLevel => [...prevLevel, idx]) }}>{item.name}</button>,
  'modal-trigger': (item) => `Modal trigger TODO ${item}`,
  'link-external': (item) => <a href={item.href} rel='noreferrer'>{item.name}</a>
}

const getMenuItems = (data, level, setLevel, accordionList = false, currentPathname, params, toggle) => {
  let list = data
  let reachLevel = 0

  if(!accordionList) {
    while(reachLevel < level.length) {
      list = list[level[reachLevel]]?.children
      reachLevel++
    }
  }

  return list.map((item, idx) => {
    return <Li key={`${level?.length}-${item.type}-${idx}`} variants={listItemVariants}>
      {itemByType[item.type](item, setLevel, idx, level, currentPathname, params, toggle)}
    </Li>
  })
}

// export const navItems = [
//   { name: 'Sweet home', href: '/?splash=no', type: 'link', altPathname: '/' },
//   { name: 'People', href: '/people', type: 'link', altPathname: '/people/[slug]' },
//   { name: 'Places', href: '/cities', type: 'link', altPathname: '/[city]' },
//   { name: 'Near me', href: '/near-me', type: 'link' },
//   { name: 'The magazine', href: '/the-magazine', type: 'link' },
// ]


let prevUrl = null
export const ModalMenu: FC<MenuProps> = memo(({ hasBanners = false, data = [] }) => {
  const { isActive, toggle } = useModal(MODALS.MENU)
  const [level, setLevel] = useState([])

  const pathname = usePathname()
  const params = useParams()
  const searchParams = useSearchParams()

  const scrollPos = useScrollTracker(1)
  const scrollTop = useMemo(() => scrollPos === 'TOP', [scrollPos])
  const animate = useMemo(() => hasBanners && scrollTop ? true : false, [hasBanners, scrollTop])

  const Items = useMemo(() => getMenuItems(data, level, setLevel, false, pathname, params, toggle), [data, level, setLevel, pathname, params, toggle])

  const goOneLevelBack = useCallback(() => {
    setLevel(prevLevel => {
      const incomingLevel = [...prevLevel]
      incomingLevel.pop()
      return incomingLevel
    })
  }, [setLevel])

  useEffect(() => {
    const url = `${pathname}?${searchParams}`
    if (!prevUrl) prevUrl = url
    else if(prevUrl !== url && isActive){
      prevUrl = url
      toggle()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, searchParams])

  return (
    <Modal isActive={isActive} close={toggle} title='Menu' xAlign='flex-start' variants={dialogVariants} dialogStyles={dialogStyles(animate)} hasHeader={false} zIndex={100} isModalFilters={level.length === 0 ? true : false} >
      {level.length > 0 && <ButtonMenuBack key={'menu-back-button'} onClick={goOneLevelBack} className='button-back'>
        <Figure media={backIconLight} fill={false} />
      </ButtonMenuBack>}
      <Ul key={`list-menu-${level?.length}`} $levelDepth={level?.length} initial='inactive' animate={'active'} exit='exit' variants={listVariants}>
        {Items}
      </Ul>
      <AnimatePresence mode='sync'>
        {isActive && <Div className='sablito' initial={{ opacity: 0, x: '100%' }} animate={{ opacity: 1, x: '0%', transition: { delay: 2, duration: 4, opacity: { duration: .1 } }}} exit={{ x: '0%', transition: { duration: 1 }}}>
          <Figure media={sablitoImage} />
        </Div>}
      </AnimatePresence>
    </Modal>
  )
})


const Ul = styled(motion.ul) <{ $levelDepth: number }>`
  align-items: flex-start;
  color: inherit;
  font-family: ${({ theme }) => theme.fonts.primaryFont};
  display: flex;
  flex-direction: column;

  a {
    text-decoration: none;
  }

  a,
  button,
  summary {
    ${({ $levelDepth }) => $levelDepth === 0 ? getP40() : getP13()}
    align-items: center;
    color: inherit;
    display: flex;
    justify-content: flex-start;
    max-height: ${vw(80, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      max-height: ${vw(80, 'nexus7')};
    }

    ${mq.greaterThan('tablet')} {
      height: ${vw(60, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      height: 60px;
    }
  }

  div {
    > dl,
    > ol,
    > ul {

      button,
      summary {
        font-weight: 500;
      }

      a {
        ${getP13()}
        color: inherit;
        font-family: ${({ theme }) => theme.fonts.primaryFont};
        text-decoration: none;
      }
    }

    li {
      &:first-child {
        margin-top: ${vw(30, 'mobile')};
      }

      &:last-child {
        margin-bottom:${vw(16, 'mobile')};
      }
    }
  }
`

const Li = styled(motion.li)`
  display: flex;
  margin-bottom: ${vw(25, 'mobile')};
  margin-top: ${vw(12, 'mobile')};
  width: 100%;

  button,
  a {
    &.active {
      color: var(--color-blue);
    }
  }
`

const Details = styled(motion.div) <any>`
  width: 100%;

  &[open] {
    height: auto;

    summary {
      figure {
        transform: rotate(180deg);
      }
    }
  }

  summary {
    justify-content: space-between;

    figure {
      max-height: 28px;
      max-width: 28px;
      transform-origin: center;
      transition: 200ms transform ease-out;
      width: ${vw(10, 'mobile')};

      ${mq.greaterThan('nexus7')} {
        width: ${vw(10, 'nexus7')};
      }

      ${mq.greaterThan('tablet')} {
        width: ${vw(10, 'desktop')};
      }

      ${mq.greaterThan('desktop')} {
        width: 10px;
      }
    }
  }
`

const ButtonMenuBack = styled.button`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  height: ${vw(48, 'mobile')};
  justify-content: center;
  margin-bottom:${vw(20, 'mobile')};
  max-width: 48px;
  transform: translateX(-30%);
  width: ${vw(48, 'mobile')};
  z-index: 2;

  ${mq.greaterThan('nexus7')} {
    height: ${vw(48, 'nexus7')};
    margin-bottom:${vw(20, 'nexus7')};
    width: ${vw(48, 'nexus7')};
  }

  ${mq.greaterThan('tablet')} {
    height: ${vw(48, 'desktop')};
    width: ${vw(48, 'desktop')};
  }

  ${mq.greaterThan('desktop')} {
    height: 48px;
    width: 48px;
  }

  figure {
    max-height: 28px;
    max-width: 28px;
    width: ${vw(20, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      width: ${vw(20, 'nexus7')};
    }

    ${mq.greaterThan('tablet')} {
      width: ${vw(20, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      width: 10px;
    }
  }
`

const dialogStyles = (_animate) => {
  return css`
    background: ${({ theme }) => theme.colors.background};
    display: block;
    margin: 0 !important;
    min-height: 100%;
    width: 100%;

    ${mq.greaterThan('tablet')} {
      display:none;
    }

    .modal__body {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      margin-top: 70px;
      overflow: hidden;
      padding: ${vw(10, 'mobile')} ${vw(40, 'mobile')} ${vw(30, 'mobile')} ${vw(5, 'mobile')};
      transition: padding 300ms ease;

      > ul,  > button:not(.button-back) {
        padding-left: ${vw(10, 'mobile')};
      }
    }

    @media screen and (min-width: 320px) and (max-width: 767px) and (orientation: landscape) {
      width: 100%;
      height: unset;
      .modal__body {
        height: unset;
        overflow-y: unset;
      }
    }
  `
}

const Div = styled(motion.div)`
  &.sablito {
    bottom: 0;
    overflow: hidden;
    position: fixed;
    right: 0;

    figure {
      height: ${vw(124, 'mobile')};
      transform: translate(-2%, 0%) scaleX(-1);
      width: ${vw(148, 'mobile')};

      ${mq.greaterThan('nexus7')} {
        height: ${vw(124, 'tablet')};
        width: ${vw(148, 'tablet')};
      }

      ${mq.greaterThan('tablet')} {
        height: ${vw(124, 'desktop')};
        width: ${vw(148, 'desktop')};
      }

      ${mq.greaterThan('desktop')} {
        height: 124px;
        width: 148px;
      }
    }

    img {
      object-fit: contain;
    }
  }
`
